/**
 * ProfileSwitcher -- Dropdown overlay for switching between profiles.
 * Shows current user avatar, list of profiles, and theme toggle.
 */

import type { AweniContext, ProfileInfo } from '../context/AweniContext';

let _el: HTMLElement | null = null;
let _ctx: AweniContext | null = null;

export function mountProfileSwitcher(container: HTMLElement, ctx: AweniContext): void {
  _ctx = ctx;

  const panel = document.createElement('div');
  panel.style.cssText = 'position:fixed;top:48px;z-index:150;display:none;'
    + (ctx.i18n.dir === 'rtl' ? 'left:8px;' : 'right:8px;');

  const card = document.createElement('div');
  card.style.cssText = 'background:var(--card-bg);border:1px solid var(--border);border-radius:12px;box-shadow:var(--shadow-lg);padding:12px;min-width:220px;max-width:280px;';

  // Current user info
  const userSection = document.createElement('div');
  userSection.style.cssText = 'padding-bottom:12px;border-bottom:1px solid var(--border);margin-bottom:8px;';

  const userName = document.createElement('p');
  userName.style.cssText = 'font-size:14px;font-weight:600;color:var(--text-primary);margin:0;';
  userName.textContent = ctx.auth.user?.name || ctx.i18n.t('profile_guest');

  const userEmail = document.createElement('p');
  userEmail.style.cssText = 'font-size:12px;color:var(--text-secondary);margin:2px 0 0;';
  userEmail.textContent = ctx.auth.user?.email || '';

  userSection.append(userName, userEmail);
  card.appendChild(userSection);

  // Profile list
  if (ctx.auth.profiles.length > 1) {
    const profilesLabel = document.createElement('p');
    profilesLabel.style.cssText = 'font-size:11px;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.05em;margin:0 0 4px;';
    profilesLabel.textContent = ctx.i18n.t('profile_switch');
    card.appendChild(profilesLabel);

    for (const profile of ctx.auth.profiles) {
      const btn = document.createElement('button');
      const isActive = profile.id === ctx.auth.activeProfile?.id;
      btn.style.cssText = 'display:flex;align-items:center;gap:8px;width:100%;padding:8px;border-radius:8px;text-align:start;'
        + (isActive ? 'background:var(--accent);color:#fff;' : '');

      const initials = document.createElement('span');
      initials.style.cssText = 'width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;flex-shrink:0;'
        + (isActive ? 'background:rgba(255,255,255,0.2);' : 'background:var(--border);color:var(--text-primary);');
      initials.textContent = profile.name.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase();

      const name = document.createElement('span');
      name.style.cssText = 'font-size:13px;font-weight:500;';
      name.textContent = profile.name;

      btn.append(initials, name);
      btn.addEventListener('click', async () => {
        await ctx.auth.switchProfile(profile.id);
        closeProfileSwitcher();
        // Refresh the page to reload with new profile context
        window.location.reload();
      });
      card.appendChild(btn);
    }

    const divider = document.createElement('div');
    divider.style.cssText = 'height:1px;background:var(--border);margin:8px 0;';
    card.appendChild(divider);
  }

  // Settings link
  const settingsBtn = document.createElement('button');
  settingsBtn.style.cssText = 'display:flex;align-items:center;gap:8px;width:100%;padding:8px;border-radius:8px;font-size:13px;color:var(--text-primary);cursor:pointer;';
  const settingsIcon = document.createElement('span');
  settingsIcon.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>';
  const settingsLabel = document.createElement('span');
  settingsLabel.textContent = ctx.i18n.t('profile_settings');
  settingsBtn.append(settingsIcon, settingsLabel);
  settingsBtn.addEventListener('click', () => {
    closeProfileSwitcher();
    ctx.navigate('#/more/settings');
  });
  card.appendChild(settingsBtn);

  // Theme toggle
  const themeBtn = document.createElement('button');
  themeBtn.style.cssText = 'display:flex;align-items:center;gap:8px;width:100%;padding:8px;border-radius:8px;font-size:13px;color:var(--text-primary);';
  themeBtn.textContent = ctx.theme.isDark
    ? ctx.i18n.t('theme_mode_light')
    : ctx.i18n.t('theme_mode_dark');
  themeBtn.addEventListener('click', () => {
    ctx.theme.toggle();
    themeBtn.textContent = ctx.theme.isDark
      ? ctx.i18n.t('theme_mode_light')
      : ctx.i18n.t('theme_mode_dark');
  });
  card.appendChild(themeBtn);

  // Logout
  if (ctx.auth.isAuthenticated) {
    const logoutBtn = document.createElement('button');
    logoutBtn.style.cssText = 'display:flex;align-items:center;gap:8px;width:100%;padding:8px;border-radius:8px;font-size:13px;color:#EF4444;margin-top:4px;';
    logoutBtn.textContent = ctx.i18n.t('auth_logout');
    logoutBtn.addEventListener('click', async () => {
      await ctx.auth.logout();
      closeProfileSwitcher();
      window.location.reload();
    });
    card.appendChild(logoutBtn);
  }

  panel.appendChild(card);
  _el = panel;
  container.appendChild(panel);

  // Close on outside click
  document.addEventListener('click', (e) => {
    if (_el && _el.style.display !== 'none' && !_el.contains(e.target as Node)) {
      // Check if the click was on the profile avatar button
      const avatar = document.querySelector('#aweni-topbar button:last-child');
      if (avatar && avatar.contains(e.target as Node)) return;
      closeProfileSwitcher();
    }
  });
}

export function openProfileSwitcher(): void {
  if (_el) {
    _el.style.display = 'block';
    _el.querySelector('div')?.classList.add('dropdown-enter');
    // Focus the first button inside
    const firstBtn = _el.querySelector('button') as HTMLElement | null;
    if (firstBtn) setTimeout(() => firstBtn.focus(), 150);
    document.addEventListener('keydown', _handleProfileKeyboard);
  }
}

export function closeProfileSwitcher(): void {
  if (_el) _el.style.display = 'none';
  document.removeEventListener('keydown', _handleProfileKeyboard);
}

/** Keyboard handler: Escape to close, Tab to trap focus. */
function _handleProfileKeyboard(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    closeProfileSwitcher();
    return;
  }
  if (e.key === 'Tab' && _el) {
    const focusables = _el.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

export function toggleProfileSwitcher(): void {
  if (_el) {
    if (_el.style.display === 'none' || !_el.style.display) {
      openProfileSwitcher();
    } else {
      closeProfileSwitcher();
    }
  }
}
